import type { BoundingArea, Natural } from "@/lib/image-crop";

/**
 * Geometry for drawing `devices.bounding_area` on a downscaled preview (POS2-1354 CP2).
 *
 * The box is stored in the original's pixel space, but the pointer moves over the rendered
 * `<img>`. Skipping the scale lands the box too small and too far up and left: a 3840x2160
 * original shown at 2000 px is a factor of 1.92 (POS2-958).
 */

export type Point = { x: number; y: number };

/** Original pixels per rendered pixel. */
export function scaleFactor(naturalWidth: number, renderedWidth: number): number {
  return naturalWidth / renderedWidth;
}

/** A pointer position over the rendered image, in the original's pixels, clamped to the frame. */
export function toNatural(
  offset: Point,
  rendered: { width: number; height: number },
  natural: Natural,
): Point {
  const scale = scaleFactor(natural.width, rendered.width);
  return {
    x: Math.min(natural.width, Math.max(0, Math.round(offset.x * scale))),
    y: Math.min(natural.height, Math.max(0, Math.round(offset.y * scale))),
  };
}

/** The box spanned by two corners, or null when it has no area. */
export function boxFromCorners(a: Point, b: Point): BoundingArea | null {
  const w = Math.abs(a.x - b.x);
  const h = Math.abs(a.y - b.y);
  if (w === 0 || h === 0) return null;
  return [Math.min(a.x, b.x), Math.min(a.y, b.y), w, h];
}

export type Corner = "nw" | "ne" | "sw" | "se";

/** The corner that stays put while `corner` is dragged. */
export function oppositeCorner(box: BoundingArea, corner: Corner): Point {
  const [x, y, w, h] = box;
  switch (corner) {
    case "nw":
      return { x: x + w, y: y + h };
    case "ne":
      return { x, y: y + h };
    case "sw":
      return { x: x + w, y };
    case "se":
      return { x, y };
  }
}

/** The server's bound on any coordinate (`MAX_FRAME_PX`), far past any camera. */
export const MAX_FRAME_PX = 100_000;

/** Why a typed box cannot be saved, or null when it can. `natural` is null with no capture. */
export function boxProblem(box: BoundingArea, natural: Natural | null): string | null {
  const [x, y, w, h] = box;
  if (![x, y, w, h].every(Number.isInteger)) return "Use whole pixels.";
  if ([x, y, w, h].some((v) => v > MAX_FRAME_PX)) return "No frame is that large.";
  if (x < 0 || y < 0) return "x and y cannot be negative.";
  if (w <= 0 || h <= 0) return "Width and height must be above 0.";
  if (natural && (x + w > natural.width || y + h > natural.height)) {
    return `The box runs past the ${natural.width}×${natural.height} frame.`;
  }
  return null;
}
